import { useEffect, useRef, useState } from "react";
import { Send, Mic, Square, Loader2 } from "lucide-react";
import { isSpeechSupported, startListening } from "../../lib/speech";

/** Bottom-pinned composer for the Conversation anchor. Mobile-first:
 *  the textarea grows to ~5 lines max and the action cluster sits
 *  flush right with thumb-sized tap targets. Voice mic is push-to-
 *  talk: hold (or tap once to start, tap again to stop) and the
 *  Web Speech API streams partial transcripts into the textarea.
 *
 *  Voice TTS reply is out of scope tonight (v2 per the spec). */
export function Composer({
  busy,
  onSend,
}: {
  busy: boolean;
  onSend: (text: string) => void;
}) {
  const [draft, setDraft] = useState("");
  const [listening, setListening] = useState(false);
  const [partial, setPartial] = useState("");
  const stopRef = useRef<(() => void) | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const speech = isSpeechSupported();

  // Auto-resize the textarea to fit content (up to a 5-line cap).
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    const max = 5 * 24 + 16;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, [draft]);

  const submit = () => {
    const text = draft.trim();
    if (!text || busy) return;
    onSend(text);
    setDraft("");
  };

  const startMic = () => {
    if (!speech || listening) return;
    const handle = startListening({
      onPartial: (text) => setPartial(text),
      onError: () => {
        setListening(false);
        setPartial("");
      },
    });
    if (!handle) {
      return;
    }
    setListening(true);
    stopRef.current = handle.stop;
    handle.done.then((finalText) => {
      setListening(false);
      stopRef.current = null;
      setPartial("");
      if (finalText) {
        setDraft((prev) => (prev ? `${prev} ${finalText}` : finalText));
        // Drop focus back to the textarea so the user can edit before
        // sending.
        setTimeout(() => taRef.current?.focus(), 30);
      }
    });
  };

  const stopMic = () => {
    stopRef.current?.();
    stopRef.current = null;
    setListening(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends; Shift+Enter inserts a newline. Mobile keyboards
    // can suppress this; the Send button is always reachable.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="sticky bottom-0 left-0 right-0 z-10 mt-4 -mx-4 sm:mx-0 px-4 sm:px-0 pb-[env(safe-area-inset-bottom)]"
    >
      <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/60 bg-white/95 dark:bg-slate-900/85 backdrop-blur shadow-lg shadow-slate-900/5 p-2 flex items-end gap-2">
        <textarea
          ref={taRef}
          value={listening && partial ? partial : draft}
          onChange={(e) => {
            if (!listening) setDraft(e.target.value);
          }}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={busy}
          placeholder={
            listening
              ? "Listening…"
              : "Ask Memso to recall, weave, or synthesize"
          }
          aria-label="Message Memso"
          className="flex-1 resize-none bg-transparent text-base text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none px-3 py-2.5 max-h-32"
        />
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {speech && (
            <button
              type="button"
              onClick={listening ? stopMic : startMic}
              aria-pressed={listening}
              aria-label={listening ? "Stop dictation" : "Dictate"}
              className={`inline-flex items-center justify-center w-11 h-11 rounded-xl transition-colors ${
                listening
                  ? "bg-rose-500 text-white shadow-md"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              {listening ? (
                <Square className="w-5 h-5" />
              ) : (
                <Mic className="w-5 h-5" />
              )}
            </button>
          )}
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            aria-label="Send"
            className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-orange-500 text-white shadow-md hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none disabled:cursor-not-allowed dark:disabled:bg-slate-700 dark:disabled:text-slate-500 transition-colors"
          >
            {busy ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </form>
  );
}
